import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';

// MUI
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import ButtonBase from '@material-ui/core/ButtonBase';
import ExitIcon from '@material-ui/icons/Clear';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

// DATA
import { useDispatch, useSelector } from 'react-redux';
import { getScheduledTransactionById } from 'data/scheduledTransactions/scheduledTransactionsSelectors';
import { CashFlowTransaction } from 'data/transactions/types';
import { getAccountsById } from "data/accounts/selectors";
import { getFreeToSpend } from 'data/freeToSpend/freeToSpendActions';

// CUSTOM
import QButton from 'components/QButton';
import useQData from 'components/QData/useQData';
import AmountField from 'components/QuickenControls/AmountField';
import ScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog';
import { negatorFromCOA } from 'data/scheduledTransactions/scheduledTransactionsUtils';
import AlertDialog from 'components/Dialogs/AlertDialog';
import PayeeField from 'components/QuickenControls/PayeeField';
import CategoryField from 'components/QuickenControls/CategoryField';
import AccountField from 'components/QuickenControls/AccountField';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 458,
    minHeight: 500,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflow: 'visible',
    borderRadius: 8,
  },
  acctAndCat: {
    width: '100%',
  },
  titleRow: {
    width: '100%',
    backgroundColor: theme.palette.greyScale[5],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: '8px 8px 0 0',
  },
  title: {
    padding: 12,
  },
  linkTransaction: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.greyScale[4]}`,
    padding: 8,
    marginTop: 12,
    borderRadius: 8,
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.greyScale[3]}`,
    },
  },
  exitButton: {
    height: 48,
    width: 48,
    padding: 12,
    borderRadius: 24,
    '&:hover': {
      backgroundColor: theme.applyOpacityToHex(theme.palette.greyScale[0], 0.1),
    },
  },
  arrowContainer: {
    height: 32,
    width: 32,
    backgroundColor: theme.palette.others.link,
    borderRadius:'50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: 24,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  textField: {
    width: '100%',
    '& label.Mui-focused': {
      color: theme.palette.greyScale[0],
    },
    '& label': {
      color: theme.palette.greyScale[0],
    },

    '& input': {
      fontSize: 14,
      paddingBottom: 4,
      '&::placeholder': {
        color: 'black',
        opacity: '100%',
      },
    },
  },
  wrapper: {
    padding: '12px 0 12px 0',
    width: '100%',
  },
  payee: {
    width: '100%',
    paddingBottom: 20,
  },
  amountInput: {
    textAlign: 'left',
    // fontWeight: 500,
  },
  datePicker: {
    width: '100%',
    paddingBottom: 24,
    '& .react-datepicker-wrapper': {
      width: '100%',
    },
    '& .react-datepicker__input-container': {
      width: '100%',
    },
  },
  datePickerEditTxn: {
    width: '50%',
    marginLeft: 12,
    '& .react-datepicker-wrapper': {
      width: '100%',
    },
    '& .react-datepicker__input-container': {
      width: '100%',
    },
  },
  calIcon: {
    transform: 'translate(-8px, -8px)',
    color: theme.applyOpacityToHex(theme.palette.greyScale[2], 0.8),
  },
  checkBox: {
    marginLeft: -8,
  },
  checkLabel: {
    fontSize: 14,
    // fontWeight: 500,
    paddingLeft: 4,
  },
  editLink: {
    color: theme.palette.others.link,
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.others.linkDark,
    },
  },

  actions: {
    width: '100%',
    paddingTop: 24,
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  deleteButton: {
    position: 'absolute',
    left: 0,
    marginLeft: 0,
    color: theme.palette.others.negative,
    borderColor: theme.palette.others.negative,
  },
}));

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function EditReminderDialog(props) {
  const classes = useStyles();
  const { txn, isIncome, onClose, linkTxn, context, ...otherProps } = props;
  const { qDataSaveTransactions, qDataDeleteTransactions } = useQData(context);

  const isPendingScheduledTxn = txn.source === 'SCHEDULED_TRANSACTION_PENDING';

  const negator = negatorFromCOA(txn.coa);
  const showInitialSign = txn.amount ? Math.sign(txn.amount) !== negator : undefined;
  const initialAmount = !Number.isNaN(txn.amount) ? String(negator < 0 ? Math.abs(txn.amount) : txn.amount) : undefined;

  // +++     State values     +++
  const [amount, setAmount] = useState(initialAmount);
  const [date, setDate] = useState(moment(txn.postedOn));
  const [excludeReports, setExcludeReports] = useState(Boolean(txn.isExcludedFromReports));
  const [excludeF2S, setExcludeF2S] = useState(Boolean(txn.isExcludedFromF2S));

  const [edit, setEdit] = useState(false);
  const [dirtyDialog, setDirtyDialog] = useState(false);
  const [payee, setPayee] = useState(txn.payee);
  const [coa, setCoa] = useState(txn.coa);
  const [accountId, setAccount] = useState(txn.accountId);

  const model = useSelector((state) => getScheduledTransactionById(state, txn.stModelId));
  const accounts = useSelector(getAccountsById);
  const accountName = accounts.get(txn.accountId).name;
  const signedAmt = (amount.includes('+') || amount.includes('-')) ? Number(amount) : Number(amount) * negator;
  const isDirty = (Number(txn.amount) !== signedAmt) || (date.toISOString() !== txn.postedOn)
    || (excludeReports !== Boolean(txn.isExcludedFromReports)) || (excludeF2S !== Boolean(txn.isExcludedFromF2S)
    || (txn.coa !== coa || txn.accountId !== accountId || txn.payee !== payee));
  const dispatch = useDispatch();

  const openEdit = () => setEdit(true);
  const closeEditDialog = (editDirty) => {
    if (editDirty) {
      setDirtyDialog(true);
    } else {
      setEdit(false);
    }
  };

  const closeCheck = () => {
    if (isDirty) {
      setDirtyDialog(true);
    } else {
      onClose();
    }
  };

  const closeAndLink = () => {
    closeCheck();
    linkTxn();
  };

  const onReportsClick = () => {
    const nextExclude = !excludeReports;
    if (nextExclude) setExcludeF2S(true);
    setExcludeReports(!excludeReports);
  };
  const onF2SClick = () => {
    setExcludeF2S(!excludeF2S);
  };
  const amountChange = (e) => setAmount(e.target.value);

  const onSave = () => {
    const txnToSave = new CashFlowTransaction({
      ...txn.toJS(),
      isExcludedFromF2S: excludeF2S,
      isExcludedFromReports: excludeReports,
      postedOn: date.toISOString(),
      amount: (Number(signedAmt.toFixed(2))),
      tags: txn.tags,
      coa,
      accountId,
      payee,
    });
    qDataSaveTransactions([txnToSave]);
    onClose();
  };
  const onDelete = () => {
    qDataDeleteTransactions([txn]);
    onClose();
  };

  return (
    <>
      <Dialog
        open={!edit}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.root }}
        onClose={closeCheck}
        {...otherProps}
      >
        <div className={classes.titleRow}>
          <Typography variant="h6" className={classes.title} noWrap>
            {isPendingScheduledTxn ? 'Edit Reminder' : 'Edit Transaction'}
          </Typography>
          <ButtonBase className={classes.exitButton} onClick={closeCheck} focusRipple >
            <ExitIcon />
          </ButtonBase>
        </div>

        <div className={classes.content}>
          {isPendingScheduledTxn ?
            <div className={classes.payee}>
              <Typography variant="h6" noWrap>
                {txn.payee}
              </Typography>
              <Typography variant='body2'>
                {accountName}
              </Typography>
            </div>
            :
            <div className={classes.wrapper}>
              <PayeeField
                editable
                name="edit-reminder-payee-field"
                label='Payee Name'
                variant={'h6'}
                value={{ payee: (payee || '') }}
                onChange={(event) => (event && event.target.value) && setPayee(event.target.value.name)}
                fontSize="14px"
                width='100%'
                bulkEdit
                classes={{root: classes.textField}}
              />
            </div>
          }
          <div style={isPendingScheduledTxn ? {width: '100%'} : {display: 'flex', flexDirection: 'row', padding: '12px 0 12px 0'}}>
            <AmountField
              value={amount}
              showInitialSign={showInitialSign}
              optimisticSign
              showAmountAdornment
              id="Reminder_Amount"
              label="Amount"
              currencySymbol="USD"
              amountType="amount"
              fullWidth
              editable
              autoComplete="off"
              style={isPendingScheduledTxn ? { paddingBottom: 24 } : {width: '50%'}}
              onChange={amountChange}
              InputProps={{
                classes: { input: classes.amountInput },
              }}
            />

            <div className={isPendingScheduledTxn ? classes.datePicker : classes.datePickerEditTxn}>
              <DatePicker
                id="next-date"
                selected={date}
                onChange={(momentDate) => {
                  if (momentDate) {
                    setDate(momentDate);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                  }
                }}
                allowSameDay
                disabledKeyboardNavigation
                customInputRef="inputRef"
                style={isPendingScheduledTxn ? { width: '100%' } : {width: '50%'}}
                customInput={
                  <TextField
                    InputProps={{
                      autoComplete: 'off',
                      endAdornment: <CalendarIcon className={classes.calIcon}/>,
                      classes: { input: classes.amountInput },
                    }}
                    label="Date"
                    fullWidth
                  />
                }
              />
            </div>
          </div>
          {!isPendingScheduledTxn &&
          <div className={classes.acctAndCat}>
            <div className={classes.wrapper}>
              <AccountField
                textFieldProps={{
                  label: 'Account',
                  classes: {root: classes.textField},
                }}
                onSelected={(selectedAccount) => setAccount(selectedAccount)}
                initialAccountId={accountId}
              />
            </div>
            <div className={classes.wrapper}>
              <CategoryField
                variant='body1'
                id={txn.id}
                editable
                value={coa}
                label='Category'
                onChange={(txnCoa) => setCoa(txnCoa)}
                textFieldProps={{classes: {root: classes.textField}}}
                width={'100%'}
              />
            </div>
          </div>
          }

          {/* Ignore Checkboxes */}
          <FormControlLabel
            control={<Checkbox />}
            label="Ignore from Reports"
            labelPlacement="end"
            onChange={onReportsClick}
            checked={excludeReports}
            classes={{ root: classes.checkBox, label: classes.checkLabel }}
          />
          <FormControlLabel
            control={<Checkbox />}
            label="Ignore from Spending Plan"
            labelPlacement="end"
            disabled={excludeReports}
            checked={excludeF2S}
            onChange={onF2SClick}
            classes={{ root: classes.checkBox, label: classes.checkLabel }}
          />

          {/* caption subtitle */}
          {isPendingScheduledTxn &&
            <>
              <div className={classes.linkTransaction} onClick={closeAndLink} role='presentation'>
                <Typography variant='body1'>Link To Transaction</Typography>
                <div className={classes.arrowContainer}>
                  <ArrowForwardIcon style={{color: 'white'}}/>
                </div>
              </div>
              <Typography variant="caption" style={{ paddingTop: 24 }}>
                {`Note: Editing here will only change this reminder.
                If you would like to edit the recurring series, go to `}
                <span
                  role="button"
                  tabIndex={0}
                  className={classes.editLink}
                  onClick={openEdit}
                  onKeyPress={openEdit}
                >
                  edit series
                </span>.
              </Typography>
            </>
          }

          <div className={classes.actions}>
            <QButton
              className={classes.deleteButton}
              variant="icon"
              disabled={!txn || !txn.id}
              id="delete-reminder"
              aria-label="delete-reminder"
              onClick={onDelete}
            >
              <DeleteIcon/>
            </QButton>

            <QButton
              id='cancel-recurrence'
              variant="outlined"
              onClick={closeCheck}
            >
              Cancel
            </QButton>

            <QButton
              id='create-update-recurrence'
              variant="contained"
              disabled={!isDirty}
              onClick={onSave}
              style={{ marginLeft: 16 }}
            >
              Update
            </QButton>
          </div>
        </div>
      </Dialog>

      {edit &&
        <ScheduledTransactionEditDialog
          key={model ? (model.clientId || model.id) : 'noKey'}
          scheduledTransaction={model}
          onClose={closeEditDialog}
        />
      }

      {dirtyDialog &&
        <AlertDialog
          dialogId={'ignore this warning'}
          title="Just checking..."
          content="Are you sure you want to discard all changes?"
          onClose={(btnObj) => {
            setDirtyDialog(false);
            if (btnObj.btnPressed === 'Discard') {
              if (edit) {
                setEdit(false);
              } else {
                onClose();
              }
            }
          }}
          buttons={['Go Back', 'Discard']}
        />
      }
    </>
  );
}

EditReminderDialog.defaultProps = {
  isIncome: false,
  txn: {},
  context: '',
};

EditReminderDialog.propTypes = {
  isIncome: PropTypes.bool,
  txn: PropTypes.object,
  onClose: PropTypes.func,
  linkTxn: PropTypes.func,
  context: PropTypes.string,
};

export default EditReminderDialog;


